import { forwardRef } from "react";

const DEALER_GSTIN = "19AFQFS8958E1Z2";

const InvoicePreview = forwardRef(({ formData, subtotal, tax, total }, ref) => {
  const fmt = (v) => new Intl.NumberFormat("en-IN").format(v || 0);
  const fmtDate = (d) => {
    if (!d) return "";
    try { return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" }); }
    catch { return d; }
  };

  const toWords = (num) => {
    const ones = ["","One","Two","Three","Four","Five","Six","Seven","Eight","Nine","Ten","Eleven","Twelve","Thirteen","Fourteen","Fifteen","Sixteen","Seventeen","Eighteen","Nineteen"];
    const tens = ["","","Twenty","Thirty","Forty","Fifty","Sixty","Seventy","Eighty","Ninety"];
    if (!num || num === 0) return "Zero";
    if (num < 20) return ones[num];
    if (num < 100) return tens[Math.floor(num/10)] + (num%10 ? " " + ones[num%10] : "");
    if (num < 1000) return ones[Math.floor(num/100)] + " Hundred" + (num%100 ? " " + toWords(num%100) : "");
    if (num < 100000) return toWords(Math.floor(num/1000)) + " Thousand" + (num%1000 ? " " + toWords(num%1000) : "");
    if (num < 10000000) return toWords(Math.floor(num/100000)) + " Lakh" + (num%100000 ? " " + toWords(num%100000) : "");
    return toWords(Math.floor(num/10000000)) + " Crore" + (num%10000000 ? " " + toWords(num%10000000) : "");
  };

  const effectiveFinancier = formData.financierName === "Other" ? formData.financierNameCustom : formData.financierName;

  return (
    <div
      ref={ref}
      className="print-document bg-white text-black mx-auto"
      style={{
        fontFamily: "Arial, sans-serif",
        fontSize: "11px",
        width: "100%",
        maxWidth: "210mm",
        padding: "12mm",
        boxSizing: "border-box",
        minHeight: "277mm",
        color: "#000",
        background: "#fff",
      }}
    >
      {/* ── Header ──────────────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "2px solid #000", paddingBottom: "8px", marginBottom: "8px" }}>
        <img
          src="https://customer-assets.emergentagent.com/job_384f5a02-e73c-441b-8711-7b4aa811af91/artifacts/1llnvk1o_be6ba909-56b0-4515-9de1-7b59366d1e68_Untitled.avif"
          alt="Logo" style={{ height: "100px", objectFit: "contain" }}
          onError={(e) => { e.target.style.display = "none"; }}
        />
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "24px", fontWeight: "bold" }}>SHREE MOBILITY TRADE</div>
          <div style={{ fontSize: "10px", color: "#555" }}>(Authorised Dealer of Kinetic Green)</div>
          <div style={{ fontSize: "10px" }}>NH 14, Brahmanigram more, Rampurhat</div>
          <div style={{ fontSize: "10px", fontWeight: "600" }}>Mob: 9749430705 / 7063511705</div>
          <div style={{ fontSize: "10px", fontWeight: "600" }}>GSTIN: {DEALER_GSTIN}</div>
        </div>
      </div>

      {/* ── Doc Title ────────────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
        <div style={{ fontSize: "10px", lineHeight: "1.6" }}>
          <div><b>Invoice No.:</b> {formData.invoiceNumber}</div>
          <div><b>Date:</b> {fmtDate(formData.invoiceDate)}</div>
          {formData.deliveryDate && <div><b>Delivery:</b> {fmtDate(formData.deliveryDate)}</div>}

        </div>
        <div style={{ border: "2px solid #000", padding: "4px 16px", fontWeight: "bold", fontSize: "15px" }}>TAX INVOICE</div>
      </div>

      {/* ── Bill To / Vehicle ────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "8px" }}>
        {/* Bill To */}
        <div style={{ border: "1px solid #000", padding: "6px" }}>
          <div style={{ fontWeight: "bold", background: "#f3f3f3", margin: "-6px -6px 4px -6px", padding: "3px 6px", borderBottom: "1px solid #000", fontSize: "10px" }}>BILL TO</div>
          <div style={{ lineHeight: "1.6", fontSize: "10px" }}>
            <div><b>Name:</b> {formData.customerName || "—"}</div>
            <div><b>Address:</b> {formData.customerAddress || "—"}</div>
            <div><b>Phone:</b> {formData.customerPhone || "—"}</div>
            {formData.customerAadhaar && <div><b>Aadhaar:</b> {formData.customerAadhaar}</div>}
            {formData.customerPan && <div><b>PAN:</b> {formData.customerPan}</div>}
            {formData.customerGstin && <div><b>GSTIN:</b> {formData.customerGstin}</div>}
          </div>
        </div>
        {/* Vehicle */}
        <div style={{ border: "1px solid #000", padding: "6px" }}>
          <div style={{ fontWeight: "bold", background: "#f3f3f3", margin: "-6px -6px 4px -6px", padding: "3px 6px", borderBottom: "1px solid #000", fontSize: "10px" }}>VEHICLE DETAILS</div>
          <div style={{ lineHeight: "1.6", fontSize: "10px" }}>
            <div><b>Model:</b> {formData.vehicleModel || "—"}</div>
            <div><b>Color:</b> {formData.vehicleColor || "—"}</div>
            <div><b>Chassis No:</b> {formData.chassisNumber || "—"}</div>
            <div><b>Motor No:</b> {formData.motorNumber || "—"}</div>
            <div><b>Battery:</b> {formData.batteryMake || "—"} {formData.batteryAh || ""}</div>
            {formData.batteryNumber && <div><b>Battery No:</b> {formData.batteryNumber}</div>}
            <div><b>With Battery:</b> {formData.batteryIncluded || "Yes — With Battery"}</div>
          </div>
        </div>
      </div>

      {/* ── Price Table ──────────────────────────────────────────── */}
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "4px", fontSize: "10px" }}>
        <thead>
          <tr style={{ background: "#f3f3f3" }}>
            <th style={{ border: "1px solid #000", padding: "4px 6px", textAlign: "left", fontWeight: "bold" }}>DESCRIPTION</th>
            <th style={{ border: "1px solid #000", padding: "4px 6px", textAlign: "right", fontWeight: "bold", width: "110px" }}>AMOUNT (₹)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ border: "1px solid #000", padding: "5px 6px" }}>
              <div style={{ fontWeight: "600" }}>{formData.vehicleModel || "Electric Three Wheeler"}</div>
              <div style={{ color: "#555", fontSize: "9.5px" }}>Ex-Showroom Price</div>
            </td>
            <td style={{ border: "1px solid #000", padding: "5px 6px", textAlign: "right", fontWeight: "600" }}>₹{fmt(formData.exShowroomPrice)}</td>
          </tr>
          {parseFloat(formData.cgst) > 0 && <tr><td style={{ border: "1px solid #000", padding: "4px 6px" }}>CGST @ 2.5%</td><td style={{ border: "1px solid #000", padding: "4px 6px", textAlign: "right" }}>₹{fmt(formData.cgst)}</td></tr>}
          {parseFloat(formData.sgst) > 0 && <tr><td style={{ border: "1px solid #000", padding: "4px 6px" }}>SGST @ 2.5%</td><td style={{ border: "1px solid #000", padding: "4px 6px", textAlign: "right" }}>₹{fmt(formData.sgst)}</td></tr>}
          {parseFloat(formData.rtoCharges) > 0 && <tr><td style={{ border: "1px solid #000", padding: "4px 6px" }}>RTO / Registration Charges</td><td style={{ border: "1px solid #000", padding: "4px 6px", textAlign: "right" }}>₹{fmt(formData.rtoCharges)}</td></tr>}
          {parseFloat(formData.insuranceCharges) > 0 && (
            <tr>
              <td style={{ border: "1px solid #000", padding: "4px 6px" }}>
                Insurance Premium{formData.insuranceCompany && ` (${formData.insuranceCompany})`}
              </td>
              <td style={{ border: "1px solid #000", padding: "4px 6px", textAlign: "right" }}>₹{fmt(formData.insuranceCharges)}</td>
            </tr>
          )}
          {parseFloat(formData.accessoriesCharges) > 0 && <tr><td style={{ border: "1px solid #000", padding: "4px 6px" }}>Accessories</td><td style={{ border: "1px solid #000", padding: "4px 6px", textAlign: "right" }}>₹{fmt(formData.accessoriesCharges)}</td></tr>}
          {parseFloat(formData.otherCharges) > 0 && <tr><td style={{ border: "1px solid #000", padding: "4px 6px" }}>Other Charges</td><td style={{ border: "1px solid #000", padding: "4px 6px", textAlign: "right" }}>₹{fmt(formData.otherCharges)}</td></tr>}
          {parseFloat(formData.discount) > 0 && <tr style={{ background: "#fff5f5" }}><td style={{ border: "1px solid #000", padding: "4px 6px", color: "#c00" }}>Discount</td><td style={{ border: "1px solid #000", padding: "4px 6px", textAlign: "right", color: "#c00" }}>−₹{fmt(formData.discount)}</td></tr>}
        </tbody>
        <tfoot>
          <tr style={{ background: "#f3f3f3" }}>
            <td style={{ border: "2px solid #000", padding: "6px", fontWeight: "bold", fontSize: "12px" }}>GRAND TOTAL</td>
            <td style={{ border: "2px solid #000", padding: "6px", textAlign: "right", fontWeight: "bold", fontSize: "12px" }}>₹{fmt(total)}</td>
          </tr>
        </tfoot>
      </table>
      <div style={{ fontSize: "9px", color: "#555", marginBottom: "6px", fontStyle: "italic" }}>
        * GST @ 5% applicable on EV Three-Wheeler
      </div>

      {/* ── Amount in Words ──────────────────────────────────────── */}
      <div style={{ background: "#f9f9f9", border: "1px solid #000", padding: "5px 8px", marginBottom: "8px", fontSize: "10px" }}>
        <b>Amount in Words:</b> Rupees {toWords(Math.round(total))} Only
      </div>

      {/* ── Payment & Bank ───────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "8px" }}>
        <div style={{ border: "1px solid #000", padding: "6px", fontSize: "10px" }}>
          <div style={{ fontWeight: "bold", marginBottom: "3px" }}>PAYMENT DETAILS</div>
          <div><b>Mode:</b> {formData.paymentMode || "—"}</div>
          {formData.paymentReference && <div><b>Ref:</b> {formData.paymentReference}</div>}
          {effectiveFinancier && <div><b>Financier:</b> {effectiveFinancier}</div>}
        </div>
        <div style={{ border: "1px solid #000", padding: "6px", fontSize: "10px" }}>
          <div style={{ fontWeight: "bold", marginBottom: "3px" }}>BANK DETAILS</div>
          <div><b>A/C:</b> SHREE MOBILITY TRADE</div>
          <div><b>A/C No.:</b> 1888102000011024</div>
          <div><b>Bank:</b> IDBI BANK, Rampurhat</div>
          <div><b>IFSC:</b> IBKL0001888</div>
        </div>
      </div>

      {/* ── Notes ────────────────────────────────────────────────── */}
      {formData.notes && (
        <div style={{ background: "#f9f9f9", border: "1px solid #000", padding: "5px 8px", marginBottom: "6px", fontSize: "10px" }}>
          <b>NOTES:</b> {formData.notes}
        </div>
      )}

      {/* ── Terms ────────────────────────────────────────────────── */}
      <div style={{ marginBottom: "16px", fontSize: "9.5px" }}>
        <b>TERMS & CONDITIONS:</b>
        <ul style={{ margin: "2px 0 0 14px", padding: 0 }}>
          <li>Warranty as per manufacturer's policy on vehicle and battery.</li>
          <li>Vehicle remains showroom property until full payment is made.</li>
          <li>All disputes under Rampurhat legal jurisdiction.</li>
        </ul>
      </div>

      {/* ── Signatures ───────────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", paddingTop: "12px" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ borderTop: "1px solid #000", width: "140px", paddingTop: "3px", fontSize: "10px" }}>Customer's Signature</div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "10px", fontWeight: "600", marginBottom: "20px" }}>For, SHREE MOBILITY TRADE</div>
          <div style={{ borderTop: "1px solid #000", width: "140px", paddingTop: "3px", fontSize: "10px" }}>Authorised Signatory</div>
        </div>
      </div>
    </div>
  );
});

InvoicePreview.displayName = "InvoicePreview";
export default InvoicePreview;
